import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaSun, FaMoon, FaChevronDown } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleDropdownToggle = (menuName) => {
    setActiveDropdown(activeDropdown === menuName ? null : menuName);
  };

  const navItems = [
    { name: 'Home', path: '/', hasDropdown: false },
    { name: 'About', path: '/about', hasDropdown: false },
    { name: 'Services', path: '/services', hasDropdown: false },
    { name: 'Projects', path: '/projects', hasDropdown: false },
    { name: 'Contact', path: '/contact', hasDropdown: false },
  ];

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <nav className="header-nav">
        <div className="header-container">
          {/* Logo */}
          <Link to="/" className="header-logo" onClick={closeMenu}>
            <img src="/logo.jpg" alt="Robolog Automation" className="logo-image" />
            <span className="logo-text">Robolog Automation</span>
          </Link>

          {/* Desktop Navigation */}
          <ul className="nav-menu">
            {navItems.map((item) => (
              <li
                key={item.name}
                className={`nav-item ${item.hasDropdown ? 'has-dropdown' : ''}`}
                onMouseEnter={() => item.hasDropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => item.hasDropdown && setActiveDropdown(null)}
              >
                {item.hasDropdown ? (
                  <>
                    <Link
                      to={item.path}
                      className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      {item.name}
                      <FaChevronDown className="dropdown-icon" />
                    </Link>
                    <ul className={`dropdown-menu ${activeDropdown === item.name ? 'show' : ''}`}>
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <Link
                            to={child.path}
                            className="dropdown-link"
                            onClick={closeMenu}
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Header Actions */}
          <div className="header-actions">
            {/* Theme Toggle Button */}
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span className="theme-toggle-inner">
                {isDarkMode ? (
                  <FaSun className="theme-icon sun" />
                ) : (
                  <FaMoon className="theme-icon moon" />
                )}
              </span>
            </button>

            {/* CTA Button */}
            <Link to="/contact" className="btn-cta">
              Let's Talk
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-toggle"
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-menu">
            {navItems.map((item) => (
              <li key={item.name} className="mobile-nav-item">
                {item.hasDropdown ? (
                  <>
                    <button
                      className={`mobile-nav-link has-children ${activeDropdown === item.name ? 'expanded' : ''}`}
                      onClick={() => handleDropdownToggle(item.name)}
                    >
                      {item.name}
                      <FaChevronDown className={`mobile-dropdown-icon ${activeDropdown === item.name ? 'rotated' : ''}`} />
                    </button>
                    <ul className={`mobile-dropdown ${activeDropdown === item.name ? 'show' : ''}`}>
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <Link
                            to={child.path}
                            className="mobile-dropdown-link"
                            onClick={closeMenu}
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={closeMenu}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}

            {/* Mobile CTA Button */}
            <li className="mobile-nav-cta">
              <Link to="/contact" className="btn-cta" onClick={closeMenu}>
                Let's Talk
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;
